import { useState } from "react";
import { Link, useParams, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowLeft, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PartnerForm, type PartnerFormValues } from "@/components/wholesale/partner-form";
import { PaymentForm } from "@/components/wholesale/payment-form";
import { WholesaleStatusBadge } from "@/components/wholesale/status-badge";
import { METHOD_LABEL } from "@/components/wholesale/constants";
import {
  usePartner, usePartnerBalance, usePartnerPayments, useSavePartner, useDeletePartner,
  useWholesaleOrders, useDeletePayment, HttpError, type Partner,
} from "@/hooks/use-wholesale";
import { fmtKm, fmtPct, fmtUnixDate } from "@/lib/wholesale-format";
import { cn } from "@/lib/utils";

export function WholesalePartnerDetailPage() {
  const { partnerId } = useParams({ strict: false }) as { partnerId?: string };
  const navigate = useNavigate();
  const partner = usePartner(partnerId);
  const balance = usePartnerBalance(partnerId);
  const payments = usePartnerPayments(partnerId);
  const orders = useWholesaleOrders({ partnerId: partnerId ?? "" });
  const savePartner = useSavePartner();
  const deletePartner = useDeletePartner();
  const deletePayment = useDeletePayment();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<string | null>(null);
  // Nakon spremanja forma se puni iz onoga što je server vratio (normalizovane vrijednosti).
  const [saved, setSaved] = useState<{ partner: Partner; at: number } | null>(null);

  async function handleSave(values: PartnerFormValues) {
    try {
      const result = await savePartner.mutateAsync({ id: partnerId, ...values });
      setSaved({ partner: result, at: Date.now() });
      toast.success("Partner spremljen");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri spremanju partnera");
    }
  }

  async function handleDelete() {
    try {
      await deletePartner.mutateAsync(partnerId!);
      toast.success("Partner obrisan");
      navigate({ to: "/wholesale/partners" });
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju partnera");
    }
  }

  async function handleDeletePayment() {
    const id = paymentToDelete;
    if (!id) return;
    try {
      await deletePayment.mutateAsync(id);
      toast.success("Uplata obrisana");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju uplate");
    } finally {
      setPaymentToDelete(null);
    }
  }

  const p = partner.data;
  const bal = balance.data;
  const orderList = orders.data ?? [];
  const paymentList = payments.data ?? [];

  if (partner.isLoading) {
    return (
      <div className="p-4 flex flex-col gap-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (partner.error || !p) {
    const err = partner.error as Error | null;
    const notFound = !err || (err instanceof HttpError && err.status === 404);
    return (
      <div className="p-4 flex flex-col gap-3">
        <Link to="/wholesale/partners" className="text-xs text-muted-foreground hover:underline">← Partneri</Link>
        <p className={cn("text-xs", notFound ? "text-muted-foreground" : "text-destructive")}>
          {notFound ? "Partner nije pronađen." : `Partner nije učitan: ${err.message}`}
        </p>
      </div>
    );
  }

  const listError = (label: string, e: unknown) => (
    <div className="ring-1 ring-destructive/30 p-6 text-center text-xs text-destructive">
      {label}: {(e as Error).message}
    </div>
  );

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="icon-xs" aria-label="Nazad na partnere" asChild>
          <Link to="/wholesale/partners">
            <ArrowLeft className="h-3.5 w-3.5" />
          </Link>
        </Button>
        <h2 className="text-sm font-medium">{p.name}</h2>
        <Badge variant="outline">{p.type === "company" ? "Firma" : "Fizičko lice"}</Badge>
        <span className="text-xs text-muted-foreground">Rabat {fmtPct(p.discountPct)}</span>
        <span className={cn("text-xs tabular-nums", (bal?.balance ?? 0) > 0 && "text-red-600 font-medium")}>
          Saldo {fmtKm(bal?.balance ?? 0)}
        </span>
        {bal && bal.overdueOrders.length > 0 && (
          <Badge variant="destructive">{bal.overdueOrders.length} dospjelo</Badge>
        )}
      </div>

      <Tabs defaultValue="podaci">
        <TabsList variant="line">
          <TabsTrigger value="podaci">Podaci</TabsTrigger>
          <TabsTrigger value="narudzbe">Narudžbe</TabsTrigger>
          <TabsTrigger value="uplate">Uplate</TabsTrigger>
        </TabsList>

        <TabsContent value="podaci" className="flex flex-col gap-3 max-w-3xl">
          <PartnerForm
            initial={saved?.partner ?? p}
            resetKey={saved?.at}
            onSubmit={handleSave}
            submitting={savePartner.isPending}
            partnerId={p.id}
          />
          <div className="flex justify-end">
            <Button variant="outline" size="sm" className="gap-1.5 text-destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="h-3.5 w-3.5" />
              Obriši partnera
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="narudzbe" className="flex flex-col gap-2">
          {orders.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : orders.error ? (
            listError("Narudžbe nisu učitane", orders.error)
          ) : (
            <div className="ring-1 ring-foreground/10 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                    <th className="py-2 px-2 text-left font-medium">Broj</th>
                    <th className="py-2 px-2 text-left font-medium">Datum</th>
                    <th className="py-2 px-2 text-left font-medium">Status</th>
                    <th className="py-2 px-2 text-right font-medium">Iznos</th>
                    <th className="py-2 px-2 text-right font-medium">Plaćeno</th>
                  </tr>
                </thead>
                <tbody>
                  {orderList.map((o) => (
                    <tr key={o.id} className="border-b last:border-b-0 hover:bg-muted/50 transition-colors">
                      <td className="py-2.5 px-2">
                        <Link
                          to="/wholesale/orders/$orderId"
                          params={{ orderId: o.id }}
                          className="font-medium tabular-nums hover:underline underline-offset-2"
                        >
                          {o.number}
                        </Link>
                      </td>
                      <td className="py-2.5 px-2 text-muted-foreground tabular-nums">{fmtUnixDate(o.createdAt)}</td>
                      <td className="py-2.5 px-2"><WholesaleStatusBadge status={o.status} /></td>
                      <td className="py-2.5 px-2 text-right tabular-nums">{fmtKm(o.total)}</td>
                      <td className="py-2.5 px-2 text-right tabular-nums text-muted-foreground">{fmtKm(o.paid)}</td>
                    </tr>
                  ))}
                  {orderList.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Nema narudžbi</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="uplate" className="flex flex-col gap-3 max-w-3xl">
          <PaymentForm
            partnerId={p.id}
            orders={orderList.map((o) => ({ id: o.id, number: o.number, total: o.total, paid: o.paid, status: o.status }))}
          />
          {payments.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : payments.error ? (
            listError("Uplate nisu učitane", payments.error)
          ) : (
            <div className="ring-1 ring-foreground/10 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                    <th className="py-2 px-2 text-left font-medium">Datum</th>
                    <th className="py-2 px-2 text-right font-medium">Iznos</th>
                    <th className="py-2 px-2 text-left font-medium">Način</th>
                    <th className="py-2 px-2 text-left font-medium">Narudžba</th>
                    <th className="py-2 px-2 text-left font-medium">Napomena</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {paymentList.map((pay) => (
                    <tr key={pay.id} className="border-b last:border-b-0">
                      <td className="py-2.5 px-2 tabular-nums">{fmtUnixDate(pay.paidAt)}</td>
                      <td className="py-2.5 px-2 text-right tabular-nums font-medium">{fmtKm(pay.amount)}</td>
                      <td className="py-2.5 px-2 text-muted-foreground">{METHOD_LABEL[pay.method] ?? pay.method}</td>
                      <td className="py-2.5 px-2 text-muted-foreground tabular-nums">{pay.orderNumber || "—"}</td>
                      <td className="py-2.5 px-2 text-muted-foreground">{pay.note || "—"}</td>
                      <td className="py-2.5 px-1 text-right">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label="Obriši uplatu"
                          disabled={deletePayment.isPending}
                          onClick={() => setPaymentToDelete(pay.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {paymentList.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Nema uplata</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Obrisati partnera?</AlertDialogTitle>
            <AlertDialogDescription>
              Partner „{p.name}" se briše trajno. Partner s narudžbama se ne može obrisati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Odustani</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deletePartner.isPending}>Obriši</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={paymentToDelete !== null} onOpenChange={(v) => !v && setPaymentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Obrisati uplatu?</AlertDialogTitle>
            <AlertDialogDescription>
              Uplata se briše trajno, a saldo partnera i stanje plaćanja narudžbe se ponovo obračunavaju.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Odustani</AlertDialogCancel>
            <AlertDialogAction disabled={deletePayment.isPending} onClick={handleDeletePayment}>
              Obriši uplatu
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
